import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getApiUrl } from '@/config/api';

/**
 * Who is signed in on the company side and what they may do, from /api/company/me.
 * Menus, routes and buttons read this through useCan() and <Can>.
 *
 * Hiding something here is for usability. The server decides on every request.
 *
 * Only a team login is ever limited. Owners, internal admins and vendor recruiters
 * open the same screens and keep everything they had, including if /api/company/me
 * fails or doesn't exist yet. A team member sees nothing limited until their access
 * has loaded, so a page never flashes actions they can't use.
 */

// A role change shows up in the menus on the next page after this long.
const REFRESH_AFTER_MS = 60 * 1000;

const NOT_LIMITED = {
  ready: true,
  failed: false,
  isTeamMember: false,
  teamEnabled: false,
  scopedRolesEnabled: false,
  scope: 'company',
  user: null,
  role: null,
  permissions: new Set(),
};

const readToken = () => {
  try {
    return sessionStorage.getItem('employer_token') || localStorage.getItem('employer_token') || '';
  } catch {
    return '';
  }
};

const readStoredMember = () => {
  try {
    return JSON.parse(localStorage.getItem('company_user') || 'null');
  } catch {
    return null;
  }
};

/**
 * For a different kind of sign-in in the same browser, such as an internal admin or a
 * vendor recruiter. The team token is sent with every API call, so left in place it
 * would keep deciding what the new login may do.
 */
export const forgetTeamLogin = () => {
  try {
    if (!localStorage.getItem('company_user')) return;
    for (const key of ['company_user', 'employer_token', 'employer_email', 'companyAccountId', 'company_name', 'company_token']) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  } catch {
    /* storage can be blocked; the server has already dropped the claim */
  }
};

const startingState = () => {
  const member = readStoredMember();
  if (!member) return NOT_LIMITED;
  return { ...NOT_LIMITED, ready: false, isTeamMember: true, user: member };
};

const stateFrom = (me) => ({
  ready: true,
  failed: false,
  isTeamMember: !me.is_owner,
  teamEnabled: Boolean(me.team_enabled),
  scopedRolesEnabled: Boolean(me.scoped_roles_enabled),
  scope: me.scope || 'company',
  user: me.is_owner ? null : me.user,
  role: me.role || null,
  permissions: new Set(me.permissions || []),
});

const canWith = (state, permission) => {
  if (!permission || !state.isTeamMember) return true;
  if (!state.ready) return false;
  const wanted = Array.isArray(permission) ? permission : [permission];
  return wanted.some((key) => state.permissions.has(key));
};

const CompanyAccessContext = createContext({
  ...NOT_LIMITED,
  can: () => true,
  retry: () => {},
});

export function CompanyAccessProvider({ children }) {
  const { pathname } = useLocation();
  const [known, setKnown] = useState(() => ({ token: readToken(), access: startingState() }));
  const loaded = useRef({ token: '', at: 0 });

  // Sign-in and sign-out don't share state with this provider, but both change the
  // page, which renders this again. Comparing the token here rather than in an effect
  // means the first render of the next page already has the right limits: a child's
  // effects run before this provider's, so it would otherwise start loading unlimited.
  const token = readToken();
  const state = known.token === token ? known.access : startingState();

  const load = useCallback(async (forToken) => {
    loaded.current = { token: forToken, at: Date.now() };
    try {
      const response = await fetch(getApiUrl('/api/company/me'), { credentials: 'include' });
      if (loaded.current.token !== forToken) return;
      if (!response.ok) throw new Error(`company/me ${response.status}`);
      const me = await response.json();
      if (loaded.current.token === forToken) setKnown({ token: forToken, access: stateFrom(me) });
    } catch {
      if (loaded.current.token !== forToken) return;
      loaded.current.at = 0;
      // Keep access that already loaded. Only a first load can leave a person stuck.
      setKnown((current) => (
        current.access.isTeamMember && !current.access.ready
          ? { ...current, access: { ...current.access, failed: true } }
          : current
      ));
    }
  }, []);

  useEffect(() => {
    if (token !== loaded.current.token) {
      setKnown({ token, access: startingState() });
      loaded.current = { token, at: 0 };
    }
    if (token && Date.now() - loaded.current.at > REFRESH_AFTER_MS) load(token);
  }, [token, pathname, load]);

  const retry = useCallback(() => {
    const current = readToken();
    if (!current) return;
    setKnown({ token: current, access: startingState() });
    load(current);
  }, [load]);

  const value = useMemo(
    () => ({ ...state, can: (permission) => canWith(state, permission), retry }),
    [state, retry]
  );

  return <CompanyAccessContext.Provider value={value}>{children}</CompanyAccessContext.Provider>;
}

export const useCompanyAccess = () => useContext(CompanyAccessContext);

/** True if the person holds the permission, or any of a list of them. */
export const useCan = (permission) => useCompanyAccess().can(permission);

export function Can({ permission, children, fallback = null }) {
  return useCan(permission) ? children : fallback;
}
